import * as React from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface PasswordFieldProps
  extends Omit<React.ComponentProps<"input">, "type"> {
  /** Renders the leading lock icon. */
  withLockIcon?: boolean;
  /** Paints the border red — pair it with a message below the field. */
  invalid?: boolean;
  containerClassName?: string;
}

/**
 * Password input with a show/hide toggle, shared by every screen that asks for
 * a password so the affordance and the a11y labels stay identical.
 */
export const PasswordField = React.forwardRef<
  HTMLInputElement,
  PasswordFieldProps
>(function PasswordField(
  { className, containerClassName, withLockIcon = true, invalid, ...props },
  ref,
) {
  const [visible, setVisible] = React.useState(false);

  return (
    <div className={cn("relative", containerClassName)}>
      {withLockIcon && (
        <Lock
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
      )}
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        aria-invalid={invalid || undefined}
        className={cn(
          "pr-10",
          withLockIcon && "pl-9",
          invalid && "border-destructive focus-visible:ring-destructive",
          className,
        )}
        {...props}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});
